/**
 * Task timer + manual time-entry panel (Part 7, A7.2).
 */
import { useEffect, useState, useRef } from 'react'
import { toast } from 'sonner'
import { Play, Square, Plus, Loader2, Trash2, Clock } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Switch } from '../ui/switch'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { timeTrackingService, type TimeEntry } from '../../services/timeTracking'
import type { ApiErrorLike } from '../../utils/apiError'

interface Props {
  taskId: number
}

function fmtDuration(seconds: number) {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export function TaskTimerPanel({ taskId }: Props) {
  const [entries, setEntries] = useState<TimeEntry[]>([])
  const [running, setRunning] = useState<TimeEntry | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showManual, setShowManual] = useState(false)
  const [manual, setManual] = useState({ date: new Date().toISOString().slice(0, 10), hours: '0.5', description: '', billable: false })
  const [busy, setBusy] = useState(false)
  const tickRef = useRef<number | null>(null)

  useEffect(() => { load() }, [taskId])

  useEffect(() => {
    if (!running?.started_at) { setElapsed(0); return }
    const start = new Date(running.started_at).getTime()
    const tick = () => setElapsed((Date.now() - start) / 1000)
    tick()
    tickRef.current = window.setInterval(tick, 1000)
    return () => { if (tickRef.current) window.clearInterval(tickRef.current) }
  }, [running])

  async function load() {
    setLoading(true)
    try {
      const [list, run] = await Promise.all([
        timeTrackingService.list({ task_id: taskId }),
        timeTrackingService.running(),
      ])
      setEntries(list.filter(e => e.ended_at || !e.started_at))
      setRunning(run && run.task_id === taskId ? run : null)
    } catch { toast.error('Failed to load time entries') }
    finally { setLoading(false) }
  }

  async function startTimer() {
    setBusy(true)
    try {
      const te = await timeTrackingService.startTimer(taskId, {})
      setRunning(te)
      toast.success('Timer started')
    } catch (eRaw) {
      const e = eRaw as ApiErrorLike
      toast.error(e?.response?.data?.detail || 'Could not start timer')
    } finally { setBusy(false) }
  }

  async function stopTimer() {
    if (!running) return
    setBusy(true)
    try {
      await timeTrackingService.stopTimer(running.id)
      setRunning(null)
      toast.success('Timer stopped')
      load()
    } catch { toast.error('Failed to stop timer') }
    finally { setBusy(false) }
  }

  async function saveManual() {
    const hours = parseFloat(manual.hours)
    if (!hours || hours <= 0) { toast.error('Hours must be > 0'); return }
    setBusy(true)
    try {
      await timeTrackingService.create({
        task_id: taskId, date: manual.date, hours,
        description: manual.description || undefined, billable: manual.billable,
      })
      setShowManual(false)
      setManual({ date: new Date().toISOString().slice(0, 10), hours: '0.5', description: '', billable: false })
      load()
    } catch { toast.error('Could not save entry') }
    finally { setBusy(false) }
  }

  async function deleteEntry(id: number) {
    try { await timeTrackingService.remove(id); load() }
    catch { toast.error('Delete failed') }
  }

  const totalHours = entries.reduce((sum, e) => sum + Number(e.hours || 0), 0)

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="size-4 animate-spin" /></div>

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="size-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Time</h3>
          <span className="text-xs text-muted-foreground">{totalHours.toFixed(2)}h logged</span>
        </div>
        <div className="flex items-center gap-2">
          {running ? (
            <Button size="sm" variant="destructive" onClick={stopTimer} disabled={busy} className="min-h-[44px]">
              <Square className="size-3.5 mr-1" /> Stop {fmtDuration(elapsed)}
            </Button>
          ) : (
            <Button size="sm" onClick={startTimer} disabled={busy} className="min-h-[44px]">
              <Play className="size-3.5 mr-1" /> Start timer
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setShowManual(true)} className="min-h-[44px]">
            <Plus className="size-3.5 mr-1" /> Manual
          </Button>
        </div>
      </div>

      {entries.length > 0 && (
        <ul className="space-y-1 max-h-48 overflow-y-auto">
          {entries.slice(0, 10).map(e => (
            <li key={e.id} className="flex items-center justify-between rounded border px-2 py-1 text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono">{Number(e.hours).toFixed(2)}h</span>
                <span className="text-muted-foreground">{e.date}</span>
                {e.billable && <span className="text-green-600">·  billable</span>}
                {e.description && <span className="text-muted-foreground truncate">— {e.description}</span>}
              </div>
              <button onClick={() => deleteEntry(e.id)} className="text-muted-foreground hover:text-destructive" aria-label="Delete entry">
                <Trash2 className="size-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={showManual} onOpenChange={setShowManual}>
        <DialogContent>
          <DialogHeader><DialogTitle>Log Time</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="te-date">Date</Label>
              <Input id="te-date" type="date" value={manual.date} onChange={e => setManual(m => ({ ...m, date: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="te-hours">Hours</Label>
              <Input id="te-hours" type="number" step="0.25" min="0.01" value={manual.hours}
                onChange={e => setManual(m => ({ ...m, hours: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="te-desc">Description</Label>
              <Input id="te-desc" value={manual.description} onChange={e => setManual(m => ({ ...m, description: e.target.value }))}
                placeholder="What did you work on?" className="mt-1" />
            </div>
            <div className="flex items-center gap-3">
              <Switch id="te-billable" checked={manual.billable} onCheckedChange={v => setManual(m => ({ ...m, billable: v }))} />
              <Label htmlFor="te-billable">Billable</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManual(false)}>Cancel</Button>
            <Button onClick={saveManual} disabled={busy}>{busy && <Loader2 className="size-3.5 animate-spin mr-1" />} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
